import { cn } from "@/lib/utils"

import { meetsTarget, SUMMARY_ROWS, type EvalRun } from "./eval-runs-data"
import { hasScopeFigures, pairFigure } from "./scope"
import { ExcludedCounts, KpiTable, PairedFigure, ScopeSwitch } from "./scope-controls"
import { useExcludeOutOfScope } from "./use-scope-preference"

/** A run's figures, over every mark or over the in-scope marks as the viewer chose. */
export function EvalRunFigures({ run }: { run: EvalRun }) {
  const [preferExclude, setExclude] = useExcludeOutOfScope()
  const available = hasScopeFigures(run.summary)
  const exclude = preferExclude && available

  return (
    <>
      <section
        className="rounded-lg border border-border bg-surface p-4"
        aria-labelledby="eval-figures"
      >
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <h2 id="eval-figures" className="text-body font-semibold text-text">
            Figures{exclude ? ", in-scope marks" : ""}
          </h2>
          <ScopeSwitch checked={preferExclude} onChange={setExclude} available={available} />
        </div>
        {run.summaryRecomputed.length > 0 && (
          <p className="mb-2 text-caption text-text-3">{recomputedNote(run.scopeDecisions)}</p>
        )}
        <table className="w-full text-body-sm">
          <thead className="sr-only">
            <tr>
              <th>Figure</th>
              <th>Target</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {SUMMARY_ROWS.map((row) => {
              const pair = pairFigure(run.summary, row.key, exclude)
              // The target is held against whichever figure is shown, under its own key.
              const ok = meetsTarget(row.key, pair.primary)
              return (
                <tr key={row.key} className="border-b border-border-soft last:border-0">
                  <td className="py-2 pr-4 text-text">{row.label}</td>
                  <td className="w-24 py-2 pr-4 text-right text-caption whitespace-nowrap text-text-3">
                    {row.target ? `target ${row.target}` : ""}
                  </td>
                  <td
                    className={cn(
                      "py-2 text-right font-medium whitespace-nowrap tabular-nums",
                      ok === true && "text-master",
                      ok === false && "text-err",
                      ok === null && "text-text",
                    )}
                  >
                    <PairedFigure pair={pair} format={row.format} />
                    {ok !== null && (
                      <span className="sr-only">{ok ? " meets target" : " below target"}</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {available && <ExcludedCounts summary={run.summary} />}
        <p className="mt-3 text-caption text-text-3">{run.method}</p>
      </section>
      <KpiTable summary={run.summary} exclude={exclude} />
    </>
  )
}

/**
 * Says the in-scope figures are no longer those the run was published with, and why. Only those:
 * a decision moves no all-marks figure.
 */
function recomputedNote(decisions: number | null): string {
  if (decisions === null) return "In-scope figures updated after admin scope decisions"
  const n = `${String(decisions)} admin ${decisions === 1 ? "decision" : "decisions"}`
  return `In-scope figures updated after ${n}`
}
